import { useMemo, useState } from "react"
import { createFileRoute, Link } from "@tanstack/react-router"
import { useQuery } from "convex/react"
import { api } from "../../convex/_generated/api"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Chip } from "@/components/chips"
import { EmptyState } from "@/components/empty-state"
import { EntityRow } from "@/components/entity-row"
import { ListPageShell } from "@/components/list-page-shell"
import { PageHeader } from "@/components/page-header"
import { StatusPill } from "@/components/status-pill"
import { jobWorkModeLabel } from "@/lib/employment"
import { formatSalary } from "@/lib/salary"
import { useCompanyCapabilities } from "@/lib/company-capabilities"
import { ShareJobButton } from "@/features/jobs"

export const Route = createFileRoute("/recruit/jobs")({
  component: EmployerJobs,
})

type StatusFilter = "all" | "open" | "closed"
type SortOrder = "newest" | "most_applicants"

function applicantLabel(count: number) {
  if (count === 0) return "No applicants"
  if (count === 1) return "1 applicant"
  return `${count} applicants`
}

function EmployerJobs() {
  const jobs = useQuery(api.jobs.listMine)
  const capabilities = useCompanyCapabilities()
  const canManageJobs = capabilities?.manageJobLifecycle === true
  const [search, setSearch] = useState("")
  const [status, setStatus] = useState<StatusFilter>("open")
  const [employmentType, setEmploymentType] = useState("all")
  const [sort, setSort] = useState<SortOrder>("newest")

  const employmentTypes = useMemo(() => {
    if (!jobs) return []
    return Array.from(new Set(jobs.map((job) => job.employmentType))).sort()
  }, [jobs])

  const filteredJobs = useMemo(() => {
    if (!jobs) return []
    const q = search.trim().toLowerCase()
    const filtered = jobs.filter((job) => {
      if (status !== "all" && job.status !== status) return false
      if (employmentType !== "all" && job.employmentType !== employmentType) {
        return false
      }
      if (!q) return true
      return (
        job.title.toLowerCase().includes(q) ||
        job.location.toLowerCase().includes(q) ||
        job.skills.some((skill) => skill.toLowerCase().includes(q))
      )
    })

    return [...filtered].sort((a, b) => {
      if (sort === "most_applicants") {
        const diff = b.applicantCount - a.applicantCount
        return diff !== 0 ? diff : b._creationTime - a._creationTime
      }
      return b._creationTime - a._creationTime
    })
  }, [jobs, search, status, employmentType, sort])

  const clearFilters = () => {
    setSearch("")
    setStatus("open")
    setEmploymentType("all")
    setSort("newest")
  }

  const hasFilters =
    search.trim() !== "" ||
    status !== "open" ||
    employmentType !== "all" ||
    sort !== "newest"

  const openCount = jobs?.filter((j) => j.status === "open").length ?? 0

  return (
    <div>
      <ListPageShell
        header={
          <PageHeader
            title="Your jobs"
            subtitle={
              jobs === undefined
                ? "Loading your postings…"
                : jobs.length === 0
                  ? "Posting is free — create your first role to start receiving applicants."
                  : `${jobs.length} posting${jobs.length === 1 ? "" : "s"} · ${openCount} open · tap a role to review applicants`
            }
            actions={canManageJobs ? (
              <Link
                to="/recruit/jobs/new"
                className="home-wf-btn home-wf-btn--lime shrink-0"
              >
                New job
              </Link>
            ) : undefined}
          />
        }
        isLoading={jobs === undefined}
        emptyState={
          jobs?.length === 0 ? (
            <EmptyState
              className="mt-8"
              title="You haven't posted any jobs yet."
              description={
                canManageJobs
                  ? undefined
                  : "An owner or admin can publish roles. You can review candidates once a role is available."
              }
              action={
                canManageJobs
                  ? {
                      label: "Post your first job",
                      to: "/recruit/jobs/new",
                      emphasis: true,
                    }
                  : undefined
              }
            />
          ) : null
        }
        filters={
          <div className="mkt-filters">
            <Input
              placeholder="Search title, location, skills…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-xs"
            />
            <Select
              value={status}
              onValueChange={(value) => setStatus(value as StatusFilter)}
            >
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
                <SelectItem value="all">All status</SelectItem>
              </SelectContent>
            </Select>
            <Select value={employmentType} onValueChange={setEmploymentType}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {employmentTypes.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={sort}
              onValueChange={(value) => setSort(value as SortOrder)}
            >
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest</SelectItem>
                <SelectItem value="most_applicants">Most applicants</SelectItem>
              </SelectContent>
            </Select>
          </div>
        }
        countLine={
          <p className="mt-4 text-sm text-[color:var(--wf-mute)]">
            {filteredJobs.length === 0
              ? "No jobs match your filters"
              : `${filteredJobs.length} role${filteredJobs.length === 1 ? "" : "s"}${
                  status === "open" ? " open" : status === "closed" ? " closed" : ""
                }`}
          </p>
        }
        noMatches={
          filteredJobs.length === 0 ? (
            <EmptyState
              className="mt-2"
              title="No jobs match your filters."
              action={
                hasFilters
                  ? { label: "Clear filters", onClick: clearFilters }
                  : undefined
              }
            />
          ) : null
        }
      >
        <div className="mt-2">
          {filteredJobs.map((job) => {
            const salary = formatSalary(job)
            return (
              <EntityRow
                key={job._id}
                to="/recruit/jobs/$jobId"
                params={{ jobId: job._id }}
                title={job.title}
                chips={
                  <>
                    <StatusPill domain="job" status={job.status} />
                    {(job.featuredUntil ?? 0) > Date.now() && (
                      <Chip emphasis>Featured</Chip>
                    )}
                    <Chip emphasis={job.applicantCount > 0}>
                      {applicantLabel(job.applicantCount)}
                    </Chip>
                    <Chip>{job.employmentType}</Chip>
                    {job.workMode && <Chip>{jobWorkModeLabel(job.workMode)}</Chip>}
                    {salary && <Chip>{salary}</Chip>}
                  </>
                }
                meta={job.location}
                regions={job.remoteRegions ?? undefined}
                tags={{ items: job.skills, max: 3 }}
                cue="Manage →"
                action={
                  job.status === "open" ? (
                    <ShareJobButton
                      jobId={job._id}
                      title={job.title}
                      variant="icon"
                    />
                  ) : undefined
                }
              />
            )
          })}
        </div>
      </ListPageShell>
    </div>
  )
}
